"use client";

import { useState, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Eye, EyeOff } from "lucide-react";

type DialogMode = "create" | "enter";

interface EncryptionPasswordDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Called when dialog should close */
  onClose: () => void;
  /** Called when password is submitted */
  onSubmit: (password: string) => void;
  /** Dialog mode - create new password or enter existing */
  mode: DialogMode;
}

export function EncryptionPasswordDialog({
  isOpen,
  onClose,
  onSubmit,
  mode,
}: EncryptionPasswordDialogProps) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);

      // Validate minimum length
      if (password.length < 8) {
        setError("הסיסמה חייבת להכיל לפחות 8 תווים");
        return;
      }

      // For create mode, validate passwords match
      if (mode === "create" && password !== confirmPassword) {
        setError("הסיסמאות אינן תואמות");
        return;
      }

      onSubmit(password);
      // Reset form
      setPassword("");
      setConfirmPassword("");
    },
    [password, confirmPassword, mode, onSubmit],
  );

  const handleClose = useCallback(() => {
    setPassword("");
    setConfirmPassword("");
    setError(null);
    onClose();
  }, [onClose]);

  const toggleShowPassword = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const title = mode === "create" ? "צור סיסמת הצפנה" : "הזן סיסמת הצפנה";

  const description =
    mode === "create"
      ? "הקבלות שלך יוצפנו כדי להגן על המידע. אנא צור סיסמה חזקה (לפחות 8 תווים)."
      : "הקבלות שלך מוצפנות. אנא הזן את סיסמת ההצפנה לגישה אליהן.";

  const submitButtonText = mode === "create" ? "צור סיסמה" : "פתח קבלות";

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-md"
        dir="rtl"
        onPointerDownOutside={(e) => {
          // Prevent closing by clicking outside when password is required
          e.preventDefault();
        }}
      >
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-neutral-900">
              <Lock className="h-5 w-5" />
              {title}
            </DialogTitle>
            <DialogDescription className="text-neutral-600">
              {description}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="password" className="text-neutral-700">
                {mode === "create" ? "סיסמה חדשה" : "סיסמה"}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={
                    mode === "create" ? "לפחות 8 תווים" : "הזן סיסמה"
                  }
                  className="pr-10"
                  autoComplete="off"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={toggleShowPassword}
                  className="absolute top-1/2 left-3 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {mode === "create" && (
              <div className="grid gap-2">
                <Label htmlFor="confirmPassword" className="text-neutral-700">
                  אימות סיסמה
                </Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="הזן שוב את הסיסמה"
                    className="pr-10"
                    autoComplete="off"
                  />
                </div>
              </div>
            )}

            {error && (
              <div className="rounded bg-red-50 p-2 text-sm text-red-600">
                {error}
              </div>
            )}

            <div className="rounded bg-neutral-50 p-3 text-xs text-neutral-500">
              <strong>חשוב:</strong> הסיסמה נשמרת בזיכרון המכשיר בלבד ולא על
              השרתים שלנו. אם תשכח את הסיסמה, לא נוכל לשחזר את הקבלות המוצפנות.
            </div>
          </div>

          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button
              type="submit"
              className="w-full bg-neutral-900 text-white hover:bg-neutral-800 sm:w-auto"
            >
              {submitButtonText}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
